/**
 * DevLogPanel Component
 *
 * Non-modal panel that displays error logs for the current page.
 * Designed to help developers debug issues and export logs for Claude Code CLI.
 *
 * Features:
 * - Shows errors only for the current route
 * - Claude-friendly summary format
 * - Copy to clipboard
 * - Download as .txt file
 * - Non-blocking (doesn't prevent interaction with the page)
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { X, Copy, Download, Trash2, Bug, CheckCircle2, RefreshCw, Sparkles, Loader2 } from 'lucide-react';
import { getRawErrorLog, clearErrorLog, getErrorCount } from '@shared/utils/consoleErrorCapture';
import { apiClient } from '@shared/services/apiClient';

interface DevLogPanelProps {
  open: boolean;
  onClose: () => void;
}

export const DevLogPanel: React.FC<DevLogPanelProps> = ({ open, onClose }) => {
  const location = useLocation();
  const panelRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);
  const [errorCount, setErrorCount] = useState(0);
  const [aiSummary, setAiSummary] = useState<string | null>(null);
  const [isGeneratingAi, setIsGeneratingAi] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  // Update error count periodically
  useEffect(() => {
    if (!open) return;

    const updateCount = () => setErrorCount(getErrorCount());
    updateCount();

    const interval = setInterval(updateCount, 1000);
    return () => clearInterval(interval);
  }, [open]);

  // Close on click outside
  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    // Delay to prevent immediate close on open
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open, onClose]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, onClose]);

  // Get current route name for filtering and file naming
  const getRouteName = useCallback(() => {
    const path = location.pathname;
    // Extract route name from path (e.g., "/campaigns/123/labels" -> "labels")
    const segments = path.split('/').filter(Boolean);
    return segments[segments.length - 1] || 'home';
  }, [location.pathname]);

  // Format timestamp for file name
  const getFileTimestamp = () => {
    const now = new Date();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const hh = String(now.getHours()).padStart(2, '0');
    const min = String(now.getMinutes()).padStart(2, '0');
    return `${mm}_${dd}_${hh}_${min}`;
  };

  // Generate Claude-friendly error summary
  const generateClaudeSummary = useCallback(() => {
    const errors = getRawErrorLog();
    const currentRoute = location.pathname;
    const routeName = getRouteName();

    if (errors.length === 0) {
      return `# Dev Log - ${routeName}\n\nNo errors captured on this page.\n\nRoute: ${currentRoute}\nTimestamp: ${new Date().toISOString()}`;
    }

    // Format header
    const header = [
      `# Dev Log for Claude Code CLI`,
      ``,
      `## Context`,
      `- **Route**: ${currentRoute}`,
      `- **Page**: ${routeName}`,
      `- **Timestamp**: ${new Date().toISOString()}`,
      `- **Total Errors**: ${errors.length}`,
      `- **Browser**: ${navigator.userAgent}`,
      ``,
      `## Instructions for Claude`,
      `Please analyze these errors and suggest fixes. Focus on:`,
      `1. Root cause identification`,
      `2. Specific file/line to fix`,
      `3. Code changes needed`,
      ``,
      `---`,
      ``,
      `## Errors`,
      ``,
    ].join('\n');

    // Format each error
    const errorEntries = errors.map((error, index) => {
      const timestamp = new Date(error.timestamp).toISOString();
      const stack = error.stack ? `\n\`\`\`\n${error.stack}\n\`\`\`` : '';

      return [
        `### Error ${index + 1}`,
        `**Time**: ${timestamp}`,
        ``,
        `**Message**:`,
        `\`\`\``,
        error.message,
        `\`\`\``,
        stack ? `\n**Stack Trace**:${stack}` : '',
        ``,
      ].join('\n');
    }).join('\n---\n\n');

    return header + errorEntries;
  }, [location.pathname, getRouteName]);

  // Copy to clipboard
  const handleCopy = async () => {
    const summary = generateClaudeSummary();
    try {
      await navigator.clipboard.writeText(summary);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  // Download as .txt file
  const handleDownload = () => {
    const summary = generateClaudeSummary();
    const routeName = getRouteName().replace(/[^a-z0-9]/gi, '_');
    const timestamp = getFileTimestamp();
    const filename = `${routeName}_error_log_${timestamp}.txt`;

    const blob = new Blob([summary], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Clear errors
  const handleClear = () => {
    clearErrorLog();
    setErrorCount(0);
  };

  // Refresh error count
  const handleRefresh = () => {
    setErrorCount(getErrorCount());
  };

  // Generate AI Summary using OpenAI
  const handleAiSummary = async () => {
    const errors = getRawErrorLog();
    if (errors.length === 0) {
      setAiError('No errors to summarize');
      return;
    }

    setIsGeneratingAi(true);
    setAiError(null);
    setAiSummary(null);

    // Build a condensed error text for the API
    const errorText = errors.map((e, i) =>
      `Error ${i + 1}: ${e.message}${e.stack ? `\nStack: ${e.stack.slice(0, 500)}` : ''}`
    ).join('\n\n').slice(0, 6000);

    try {
      const response = await apiClient.post('/ai/summarize-errors', {
        errors: errorText,
        route: location.pathname,
      });
      setAiSummary(response.data.summary);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to generate summary';
      setAiError(errorMessage);
    } finally {
      setIsGeneratingAi(false);
    }
  };

  // Copy AI summary to clipboard
  const handleCopyAiSummary = async () => {
    if (!aiSummary) return;
    try {
      await navigator.clipboard.writeText(aiSummary);
    } catch (err) {
      console.error('Failed to copy AI summary:', err);
    }
  };

  if (!open) return null;

  const errors = getRawErrorLog();

  return (
    <div
      ref={panelRef}
      className="fixed top-10 right-4 w-[90vw] max-w-[480px] max-h-[70vh] bg-gradient-to-br from-dark-surface/95 to-dark-bg/85 backdrop-blur-sm border border-dark-border/30 rounded-xl shadow-2xl shadow-black/20 z-50 flex flex-col"
      role="dialog"
      aria-label="Developer Error Log"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-dark-border/30 bg-dark-header rounded-t-lg">
        <div className="flex items-center gap-2">
          <Bug size={16} className="text-red-400" />
          <span className="text-sm font-medium text-dark-text">Dev Log</span>
          <span className="text-xs px-2 py-0.5 bg-dark-bg rounded-full text-dark-muted">
            {errorCount} error{errorCount !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={handleRefresh}
            className="p-1.5 hover:bg-dark-surface/50 rounded text-dark-muted hover:text-dark-text transition-all duration-150"
            title="Refresh"
          >
            <RefreshCw size={14} />
          </button>
          <button
            onClick={handleClear}
            className="p-1.5 hover:bg-dark-surface/50 rounded text-dark-muted hover:text-red-400 transition-all duration-150"
            title="Clear all errors"
          >
            <Trash2 size={14} />
          </button>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-dark-surface/50 rounded text-dark-muted hover:text-dark-text transition-all duration-150"
            title="Close"
          >
            <X size={14} />
          </button>
        </div>
      </div>

      {/* Route Info */}
      <div className="px-4 py-2 bg-dark-bg/50 border-b border-dark-border/30 text-xs text-dark-muted">
        <span className="text-brand-400">Route:</span> {location.pathname}
      </div>

      {/* Error List */}
      <div className="flex-1 overflow-y-auto min-h-[200px] max-h-[400px]">
        {errors.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full py-8 text-dark-muted">
            <CheckCircle2 size={32} className="mb-2 text-green-500" />
            <span className="text-sm">No errors captured</span>
            <span className="text-xs mt-1">Errors will appear here as they occur</span>
          </div>
        ) : (
          <div className="p-4 space-y-3">
            {errors.map((error, index) => (
              <div
                key={`${error.timestamp}-${index}`}
                className="bg-dark-bg rounded-xl border border-dark-border/30 overflow-hidden"
              >
                <div className="px-3 py-2 bg-red-900/20 border-b border-dark-border/30 flex items-center justify-between">
                  <span className="text-xs font-medium text-red-400">
                    Error {index + 1}
                  </span>
                  <span className="text-xs text-dark-muted">
                    {new Date(error.timestamp).toLocaleTimeString()}
                  </span>
                </div>
                <div className="p-3">
                  <pre className="text-xs text-dark-text whitespace-pre-wrap break-words font-mono leading-relaxed">
                    {error.message.slice(0, 500)}
                    {error.message.length > 500 && '...'}
                  </pre>
                  {error.stack && (
                    <details className="mt-2">
                      <summary className="text-xs text-dark-muted cursor-pointer hover:text-dark-text">
                        Stack trace
                      </summary>
                      <pre className="mt-1 text-xs text-dark-muted whitespace-pre-wrap break-words font-mono max-h-32 overflow-y-auto">
                        {error.stack}
                      </pre>
                    </details>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* AI Summary Section */}
      <div className="px-4 py-3 border-t border-dark-border/30 bg-dark-bg/30">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-dark-text flex items-center gap-1.5">
            <Sparkles size={14} className="text-purple-400" />
            AI Summary (150 words)
          </span>
          <button
            onClick={handleAiSummary}
            disabled={isGeneratingAi || errors.length === 0}
            className={`flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded transition-all duration-150 ${
              isGeneratingAi
                ? 'bg-purple-900/30 text-purple-300 cursor-wait'
                : errors.length === 0
                  ? 'bg-dark-bg text-dark-muted cursor-not-allowed'
                  : 'bg-purple-600 hover:bg-purple-500 text-white'
            }`}
            title="Generate AI summary of errors"
          >
            {isGeneratingAi ? (
              <>
                <Loader2 size={12} className="animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles size={12} />
                Generate
              </>
            )}
          </button>
        </div>

        {/* AI Summary Output or Placeholder */}
        <div className="relative">
          {aiSummary ? (
            <div className="bg-dark-bg rounded border border-purple-800/50 p-3">
              <p className="text-xs text-dark-text leading-relaxed whitespace-pre-wrap">
                {aiSummary}
              </p>
              <button
                onClick={handleCopyAiSummary}
                className="absolute top-2 right-2 p-1 hover:bg-dark-surface/50 rounded text-dark-muted hover:text-purple-400 transition-all duration-150"
                title="Copy summary"
              >
                <Copy size={12} />
              </button>
            </div>
          ) : aiError ? (
            <div className="bg-red-900/20 rounded border border-red-800/50 p-2">
              <p className="text-xs text-red-400">{aiError}</p>
            </div>
          ) : (
            <div className="bg-dark-bg rounded border border-dark-border/30 p-3 text-center">
              <p className="text-xs text-dark-muted">
                Click "Generate" to create an AI summary of the errors
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Footer with Actions */}
      <div className="px-4 py-3 border-t border-dark-border/30 bg-dark-header rounded-b-lg flex items-center justify-between">
        <span className="text-xs text-dark-muted">
          Claude-ready format for debugging
        </span>
        <div className="flex items-center gap-2">
          <button
            onClick={handleCopy}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded transition-all duration-150 ${
              copied
                ? 'bg-green-900/30 text-green-400 border border-green-800'
                : 'bg-dark-bg hover:bg-dark-surface/50 text-dark-text border border-dark-border/30'
            }`}
            title="Copy to clipboard (Claude-ready format)"
          >
            {copied ? <CheckCircle2 size={14} /> : <Copy size={14} />}
            {copied ? 'Copied!' : 'Copy'}
          </button>
          <button
            onClick={handleDownload}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-brand-600 hover:bg-brand-500 text-white rounded transition-all duration-150"
            title="Download as .txt file"
          >
            <Download size={14} />
            Download
          </button>
        </div>
      </div>
    </div>
  );
};
